import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient, useProvisioningClient } from "@/lib/api";
import type { Resource, ResourceSyncResult } from "@/types";

/**
 * Resource directory hooks (Settings → Resources / the standalone /resources page).
 *
 * Talks to the resources domain:
 *   - GET   /api/resources?search=&active=&page=&page_size=  → PaginatedResources  (all roles)
 *           [&include=assignments] [&pool=delivery]          (T-07 / T-09 — used by ResourcePicker)
 *           [&role_on_project=<role>]                        (2026-10-22 — title-fits-role filter)
 *   - PATCH /api/resources/{id}                              → Resource            (admin/leadership)
 *   - POST  /api/resources/sync                              → ResourceSyncResult  (admin only — the
 *                                                              backend re-enforces RBAC / 403s)
 */

/** Query params accepted by GET /api/resources. */
export interface ResourcesParams {
  search?: string;
  page?: number;
  page_size?: number;
  active?: boolean;
  /** When true: only return active resources who submit timesheets. Used by ResourcePicker. */
  assignable?: boolean;
  /**
   * T-07: when true, sends `include=assignments` so every row carries `current_projects`
   * (one server-side query — never call `/workload` per person).
   */
  includeAssignments?: boolean;
  /**
   * T-09: `'delivery'` sends `pool=delivery` — only people in the SSM-configured delivery pool.
   * Omit for the full directory (Resources page).
   */
  pool?: "delivery";
  /**
   * 2026-10-22: sends `role_on_project` — the server narrows the list to people whose
   * `designation` fits that role (Solution Architect / Project Manager / Engineer; other roles are
   * unfiltered). Must be a wire role value (e.g. `Solution Architect`), never a display label.
   */
  roleOnProject?: string;
  /** Set false to suspend the request (e.g. a conditional fallback lookup). Default true. */
  enabled?: boolean;
}

/** Paginated envelope returned by GET /api/resources. */
export interface PaginatedResources {
  data: Resource[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

/** Body for PATCH /api/resources/{id}. Only these fields are editable (email/clockify come from sync). */
export interface UpdateResourceInput {
  name?: string;
  role_title?: string | null;
  is_active?: boolean;
}

/**
 * Fetch a page of the resource directory. `search` filters by name/email; `active` limits to active
 * rows; `page` (default 1) and `page_size` (default 20, max 100) drive pagination.
 */
export function useResources(params: ResourcesParams = {}) {
  const client = useApiClient();
  const search = params.search?.trim() ?? "";
  const { active } = params;
  const page = params.page ?? 1;
  const pageSize = params.page_size ?? 20;

  const includeAssignments = params.includeAssignments === true;
  const { pool } = params;
  const roleOnProject = params.roleOnProject || undefined;

  return useQuery<PaginatedResources>({
    queryKey: [
      "resources",
      "directory",
      {
        search,
        active,
        page,
        pageSize,
        assignable: params.assignable,
        includeAssignments,
        pool,
        roleOnProject,
      },
    ],
    enabled: params.enabled ?? true,
    queryFn: async () => {
      const query = new URLSearchParams();
      if (search) query.append("search", search);
      if (active) query.append("active", "true");
      if (params.assignable) query.append("assignable", "true");
      if (includeAssignments) query.append("include", "assignments");
      if (pool) query.append("pool", pool);
      if (roleOnProject) query.append("role_on_project", roleOnProject);
      query.append("page", String(page));
      query.append("page_size", String(pageSize));
      const response = await client.get<PaginatedResources>(
        `/api/resources?${query.toString()}`,
      );
      return response.data;
    },
  });
}

/** Update a resource's editable fields (name, role_title, is_active). Invalidates the directory. */
export function useUpdateResource() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<
    Resource,
    unknown,
    { id: string; input: UpdateResourceInput }
  >({
    mutationFn: async ({ id, input }) => {
      const response = await client.patch<Resource>(
        `/api/resources/${id}`,
        input,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["resources"] });
    },
  });
}

/** Trigger a Clockify → resources directory sync. Admin only. Invalidates the directory on success. */
export function useSyncResources() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<ResourceSyncResult>({
    mutationFn: async () => {
      const response = await client.post<ResourceSyncResult>(
        "/api/resources/sync",
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["resources"] });
    },
  });
}

// ─── T-29 / T-31 — guarded team swap ───────────────────────────────────────────

/** Reason bounds — mirror packages/resources/validation.ts SWAP_REASON_MIN / MAX (V058 CHECK). */
export const SWAP_REASON_MIN = 10;
export const SWAP_REASON_MAX = 1000;

/** Body for POST /api/projects/{projectId}/resources/swap. */
export interface SwapResourceInput {
  /** Wire enum value (e.g. `Lead Implementation Engineer`), never the display label. */
  role_on_project: string;
  from_resource_id: string;
  to_resource_id: string;
  /** 10..1000 characters after trim. Posted (escaped) to the project Slack channel. */
  reason: string;
}

/** 200 response of the swap endpoint. */
export interface SwapResourceResponse {
  swapped: true;
  role_on_project: string;
  from_resource_id: string;
  to_resource_id: string;
}

/**
 * Replace the holder of one role on a project. The endpoint lives on the Provisioning RestApi
 * (VITE_PROVISIONING_API_URL — StatelessStack is near its resource cap), so this goes through
 * `useProvisioningClient`, not the main API client. Errors (400 VALIDATION_ERROR, 404, 409
 * DUPLICATE_ASSIGNMENT) are surfaced inline by the caller, so the global error toast is suppressed.
 * Invalidates the Team tab query on success.
 */
export function useSwapResource(projectId: string) {
  const client = useProvisioningClient();
  const queryClient = useQueryClient();

  return useMutation<SwapResourceResponse, unknown, SwapResourceInput>({
    mutationFn: async (input) => {
      const response = await client.post<SwapResourceResponse>(
        `/api/projects/${encodeURIComponent(projectId)}/resources/swap`,
        input,
        { _suppressErrorToast: true },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["project-resources", projectId],
      });
    },
  });
}
